(() => {
  if (window.__perfumiarzQuickPreviewInitialized) return;
  window.__perfumiarzQuickPreviewInitialized = true;

  const ROOT_SELECTOR = '[data-preview-root]';
  const collectionProductsCache = new Map();

  const getShopRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const normalizeNavigationHandles = (value) =>
    [...new Set((value || '').split(/[|,\s]+/).map((handle) => handle.trim()).filter(Boolean))];

  const fetchCollectionProducts = (collectionHandle) => {
    if (!collectionHandle) return [];
    if (collectionProductsCache.has(collectionHandle)) return collectionProductsCache.get(collectionHandle);

    const productsRequest = (async () => {
      const limit = 250;
      const maxPages = 8;
      const products = [];
      const seenHandles = new Set();

      for (let page = 1; page <= maxPages; page += 1) {
        try {
          const response = await fetch(
            `${getShopRoot()}collections/${encodeURIComponent(collectionHandle)}/products.json?limit=${limit}&page=${page}`,
            { headers: { Accept: 'application/json' } }
          );

          if (!response.ok) break;

          const data = await response.json();
          const pageProducts = Array.isArray(data.products) ? data.products : [];
          let addedProducts = 0;

          pageProducts.forEach((product) => {
            if (!product?.handle || seenHandles.has(product.handle)) return;
            seenHandles.add(product.handle);
            products.push(product);
            addedProducts += 1;
          });

          if (pageProducts.length < limit || addedProducts === 0) break;
        } catch (error) {
          break;
        }
      }

      return products;
    })();

    collectionProductsCache.set(collectionHandle, productsRequest);
    return productsRequest;
  };

  const buildProductUrl = (productHandle) => `${getShopRoot()}products/${encodeURIComponent(productHandle)}`;

  const hydratePreviewNavigation = async (root) => {
    if (!root) return;
    if (root.dataset.previewNavigationReady === 'true' || root.dataset.previewNavigationLoading === 'true') return;

    const collectionHandles = normalizeNavigationHandles(root.getAttribute('data-preview-navigation-collections'));
    const currentProductHandle = root.getAttribute('data-preview-navigation-product-handle') || '';
    if (!collectionHandles.length || !currentProductHandle) return;

    const prevLink = root.querySelector('[data-preview-nav-link="prev"]');
    const nextLink = root.querySelector('[data-preview-nav-link="next"]');
    if (!prevLink || !nextLink) return;

    root.dataset.previewNavigationLoading = 'true';

    try {
      for (const collectionHandle of collectionHandles) {
        const products = await fetchCollectionProducts(collectionHandle);
        const currentIndex = products.findIndex((product) => product?.handle === currentProductHandle);
        if (currentIndex === -1 || products.length <= 1) continue;

        const prevProduct = products[(currentIndex - 1 + products.length) % products.length];
        const nextProduct = products[(currentIndex + 1) % products.length];
        if (!prevProduct?.handle || !nextProduct?.handle) return;

        prevLink.href = buildProductUrl(prevProduct.handle);
        nextLink.href = buildProductUrl(nextProduct.handle);
        root.dataset.previewNavigationReady = 'true';
        alignFooterFullPreviewLink(root);
        return;
      }
    } finally {
      delete root.dataset.previewNavigationLoading;
    }
  };

  const normalizeQuantity = (input) => {
    if (!input) return;
    const minValue = Number.parseInt(input.getAttribute('min'), 10) || 1;
    const nextValue = Number.parseInt(input.value, 10);

    if (Number.isNaN(nextValue) || nextValue < minValue) {
      input.value = String(minValue);
      return;
    }

    input.value = String(nextValue);
  };

  const updateSubmitState = (root, available) => {
    const submitButton = root.querySelector('.perfumiarz-quick-preview__submit');
    const variantInput = root.querySelector('[data-preview-variant-input]');

    if (!submitButton || !variantInput) return;

    const labelNode = submitButton.querySelector('span');
    const soldNode = submitButton.querySelector('.sold-out-message');
    const addLabel = submitButton.getAttribute('data-add-label') || labelNode?.textContent?.trim() || '';
    const soldLabel = submitButton.getAttribute('data-sold-label') || soldNode?.textContent?.trim() || addLabel;

    submitButton.disabled = !available;
    variantInput.disabled = !available;

    if (labelNode) labelNode.textContent = available ? addLabel : soldLabel;
    if (soldNode) soldNode.textContent = soldLabel;
  };

  const selectVariant = (root, optionButton, shouldFocus = false) => {
    if (!root || !optionButton) return;

    root.querySelectorAll('[data-preview-option]').forEach((button) => {
      button.classList.toggle('is-active', button === optionButton);
    });

    const variantId = optionButton.getAttribute('data-variant-id') || '';
    const variantInput = root.querySelector('[data-preview-variant-input]');
    if (variantInput) variantInput.value = variantId;

    const priceLabel = root.querySelector('[data-preview-price]');
    const variantPrice = optionButton.getAttribute('data-variant-price');
    if (priceLabel && variantPrice) priceLabel.textContent = variantPrice;

    const comparePriceLabel = root.querySelector('[data-preview-compare-price]');
    const variantComparePrice = optionButton.getAttribute('data-variant-compare-price') || '';
    const variantIsOnSale = optionButton.getAttribute('data-variant-on-sale') === 'true';
    if (comparePriceLabel) {
      comparePriceLabel.textContent = variantComparePrice;
      comparePriceLabel.hidden = !variantIsOnSale;
    }

    const comparePriceInput = root.querySelector('[data-preview-compare-price-input]');
    if (comparePriceInput) {
      comparePriceInput.value = optionButton.getAttribute('data-variant-compare-price-value') || '';
    }

    const isAvailable = !optionButton.hasAttribute('disabled');
    updateSubmitState(root, isAvailable);

    if (shouldFocus) optionButton.focus();
  };

  const updateQuantity = (root, direction) => {
    const quantityInput = root.querySelector('[data-preview-quantity]');
    if (!quantityInput) return;

    const minValue = Number.parseInt(quantityInput.getAttribute('min'), 10) || 1;
    const currentValue = Number.parseInt(quantityInput.value, 10) || minValue;

    if (direction === 'minus') {
      quantityInput.value = String(Math.max(minValue, currentValue - 1));
      return;
    }

    quantityInput.value = String(currentValue + 1);
  };

  const alignFooterFullPreviewLink = (root) => {
    if (!root) return;

    const footer = root.querySelector('.perfumiarz-quick-preview__footer');
    const fullPreviewLink = footer?.querySelector('.perfumiarz-quick-preview__footer-link--full');
    const quantityLabel = root.querySelector(
      '.perfumiarz-quick-preview__purchase-head .perfumiarz-quick-preview__label'
    );

    if (!footer || !fullPreviewLink || !quantityLabel) return;

    if (window.matchMedia('(max-width: 989px)').matches) {
      footer.style.setProperty('--perfumiarz-footer-full-shift', '0px');
      return;
    }

    footer.style.setProperty('--perfumiarz-footer-full-shift', '0px');

    const fullPreviewRect = fullPreviewLink.getBoundingClientRect();
    const quantityLabelRect = quantityLabel.getBoundingClientRect();
    if (fullPreviewRect.width === 0 || quantityLabelRect.width === 0) return;

    const shift = Math.round(quantityLabelRect.left - fullPreviewRect.left);
    footer.style.setProperty('--perfumiarz-footer-full-shift', `${shift}px`);
  };

  const alignAllFooterFullPreviewLinks = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const modal = root.closest('quick-add-modal');
      if (modal && !modal.hasAttribute('open')) return;
      alignFooterFullPreviewLink(root);
    });
  };

  const scheduleFooterAlignment = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => alignAllFooterFullPreviewLinks(document));
    });
  };

  const initializePreview = (root) => {
    if (!root) return;
    if (root.dataset.previewInitialized === 'true') return;
    // Keep hidden dialog images inert until this preview is explicitly opened.
    const imageTemplate = root.querySelector('template[data-preview-image]');
    if (imageTemplate) imageTemplate.replaceWith(imageTemplate.content.cloneNode(true));
    root.dataset.previewInitialized = 'true';

    const selectedOption = root.querySelector('[data-preview-option].is-active');
    const firstAvailable = root.querySelector('[data-preview-option]:not([disabled])');
    const fallbackOption = root.querySelector('[data-preview-option]');
    const initialOption = selectedOption || firstAvailable || fallbackOption;

    if (initialOption) {
      selectVariant(root, initialOption, false);
    }

    const quantityInput = root.querySelector('[data-preview-quantity]');
    normalizeQuantity(quantityInput);
  };

  document.addEventListener('click', (event) => {
    const optionButton = event.target.closest('[data-preview-option]');
    if (optionButton) {
      const root = optionButton.closest(ROOT_SELECTOR);
      if (!root || optionButton.hasAttribute('disabled')) return;
      event.preventDefault();
      selectVariant(root, optionButton, false);
      return;
    }

    const quantityButton = event.target.closest('[data-preview-qty]');
    if (quantityButton) {
      const root = quantityButton.closest(ROOT_SELECTOR);
      if (!root) return;
      event.preventDefault();
      updateQuantity(root, quantityButton.getAttribute('data-preview-qty'));
    }
  });

  document.addEventListener('change', (event) => {
    const quantityInput = event.target.closest('[data-preview-quantity]');
    if (quantityInput) normalizeQuantity(quantityInput);
  });

  document.addEventListener('input', (event) => {
    const quantityInput = event.target.closest('[data-preview-quantity]');
    if (!quantityInput) return;

    if (quantityInput.value === '') return;
    normalizeQuantity(quantityInput);
  });

  // Called by QuickAddModal after loading this file, including for AJAX cards
  // and programmatic openers. The first click must initialize the dialog too.
  window.perfumiarzQuickPreview = {
    open(root) {
      if (!root) return;
      initializePreview(root);
      hydratePreviewNavigation(root);
      scheduleFooterAlignment();
    },
  };

  window.addEventListener('resize', scheduleFooterAlignment);
})();
